var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/react/provider.tsx
import { createContext } from "react";
import { jsx } from "react/jsx-runtime";
var OriginSdkContext = createContext(null);
var OriginSdkProvider = ({
  sdk,
  children
}) => /* @__PURE__ */ jsx(OriginSdkContext.Provider, { value: sdk, children });

// src/react/hooks/use-token-launches-query.ts
import { deployments } from "@axis-finance/deployments";
import { useGetBatchAuctionLotsByBaseTokenAddressQuery } from "@axis-finance/subgraph-client";
function useTokenLaunchesQuery(chainId, baseTokenAddress) {
  const endpoint = deployments[chainId].subgraphURL;
  return useGetBatchAuctionLotsByBaseTokenAddressQuery(
    { endpoint },
    { baseTokenAddress },
    {
      select: (data) => data.batchAuctionLots.map((auction) => ({
        ...auction,
        // The subgraph info fragment filters on the latest
        // info record, so we just need to take the first array item to
        // treat info as an object instead of an array. Consumers are
        // only interested in the latest auction metadata.
        info: auction?.info?.[0]
      }))
    }
  );
}

// src/react/hooks/use-launch-query.ts
import {
  useGetBatchAuctionLotQuery
} from "@axis-finance/subgraph-client";
import { deployments as deployments3 } from "@axis-finance/deployments";

// src/core/utils.ts
import { deployments as deployments2 } from "@axis-finance/deployments";
var getDeploymentByChainId = (chainId) => {
  const deployment = deployments2[chainId];
  if (!deployment)
    throw new Error(`Unable to find deployment for chainId ${chainId}`);
  return deployment;
};
var formatChainName = (str) => str.toLowerCase().replace(/ /g, "-");
var formatAuctionId = (chainName, auctionHouseAddress, lotId) => {
  const formattedChainName = formatChainName(chainName);
  return `${formattedChainName}-${auctionHouseAddress.toLowerCase()}-${lotId}`;
};
var getLaunchId = (chainId, lotId) => {
  const deployment = getDeploymentByChainId(chainId);
  return formatAuctionId(
    deployment.name,
    deployment.addresses.batchAuctionHouse,
    lotId
  );
};

// src/react/hooks/use-sdk.ts
import { useContext } from "react";
import {
  useQuery,
  useMutation
} from "@tanstack/react-query";
var useSdkQuery = (callback, options) => {
  const sdk = useContext(OriginSdkContext);
  if (!sdk) {
    throw new Error("useSdkQuery must be used within an <OriginSdkProvider/>");
  }
  const queryKey = options?.queryKey || [callback.name];
  return useQuery({
    ...options,
    queryKey,
    queryFn: () => callback(sdk)
  });
};
var useDeferredQuery = (callback, options) => {
  const sdk = useContext(OriginSdkContext);
  if (!sdk) {
    throw new Error("useSdk must be used within an <OriginSdkProvider/>");
  }
  const mutation = useMutation({
    ...options,
    mutationFn: () => callback(sdk)
  });
  const callable = () => mutation.mutateAsync();
  Object.assign(callable, mutation);
  return callable;
};
var useSdk = () => {
  const sdk = useContext(OriginSdkContext);
  if (!sdk) {
    throw new Error("useSdk must be used within an <OriginSdkProvider/>");
  }
  return sdk;
};

// src/react/hooks/use-launch-query.ts
var useLaunchQuery = ({
  chainId,
  lotId,
  options
}) => {
  const sdk = useSdk();
  const id = getLaunchId(chainId, lotId);
  const isQueryEnabled = chainId != null && id != null && options?.enabled;
  const subgraphUrl = sdk.config?.subgraph?.[chainId]?.url ?? deployments3[chainId].subgraphURL;
  return useGetBatchAuctionLotQuery(
    { endpoint: subgraphUrl },
    { id },
    {
      enabled: isQueryEnabled,
      select: (data) => ({
        ...data.batchAuctionLot,
        // The subgraph info fragment filters on the latest
        // info record, so we just need to take the first array item to
        // treat info as an object instead of an array. Consumers are
        // only interested in the latest auction metadata.
        info: data.batchAuctionLot?.info?.[0]
      })
    }
  );
};

// src/react/hooks/use-launches-query.ts
import { useQueries } from "@tanstack/react-query";
import {
  mainnetDeployments,
  testnetDeployments
} from "@axis-finance/deployments";
import {
  GetAuctionLotsDocument,
  request
} from "@axis-finance/subgraph-client";
var defaultSubgraphUrls = (isTestnet) => (isTestnet ? testnetDeployments : mainnetDeployments).map((d) => [
  d.chain.id,
  d.subgraphURL
]);
var defaultQueryConfig = {
  document: GetAuctionLotsDocument,
  variables: {},
  queryKeyFn: (chainId) => ["launches", chainId]
};
var useLaunchesQuery = (queryConfig) => {
  const { chainIds, document, variables, queryKeyFn, isTestnet } = {
    ...defaultQueryConfig,
    ...queryConfig
  };
  const sdk = useSdk();
  const subgraphUrls = defaultSubgraphUrls(isTestnet).filter(
    ([chainId]) => chainIds == null || chainIds?.includes(chainId)
  ).map(([chainId, subgraphUrl]) => {
    return [
      chainId,
      sdk.config.subgraph?.[chainId]?.url || subgraphUrl
    ];
  });
  return useQueries({
    queries: subgraphUrls.map(([chainId, subgraphUrl]) => ({
      queryKey: queryKeyFn?.(chainId) ?? [subgraphUrl],
      queryFn: () => request(subgraphUrl, document, {
        ...variables,
        chainId
      })
    })),
    combine: (results) => {
      const data = results.filter((r) => r.data?.batchAuctionLots).flatMap((r) => r.data.batchAuctionLots).map(
        (r) => ({
          ...r,
          callbacks: r?.callbacks,
          // The subgraph info fragment filters on the latest
          // info record, so we just need to take the first array item to
          // treat info as an object instead of an array. Consumers are
          // only interested in the latest auction metadata.
          info: r?.info?.[0]
        })
      );
      return {
        data,
        queries: results,
        refetch: (opts) => results.map((r) => r.refetch(opts)),
        isSuccess: results.some((r) => r.isSuccess),
        isRefetching: results.some((r) => r.isFetching),
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        errors: results.filter((r) => r.isError).map((r) => r.error)
      };
    }
  });
};

// src/core/bid/schema.ts
import * as v2 from "valibot";
import { AuctionType } from "@axis-finance/types";

// src/core/schema.ts
import * as v from "valibot";
var BytesSchema = v.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]*$/.test(input) : false
);
var AddressSchema = v.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]{40}$/.test(input) : false
);
var IPFSCIDV1Schema = v.custom(
  (input) => typeof input === "string" ? /^b[a-z2-7]{58}$/i.test(input) : false
);

// src/core/bid/schema.ts
var schema = v2.object({
  lotId: v2.number(),
  amountIn: v2.pipe(v2.bigint(), v2.minValue(BigInt(1))),
  amountOut: v2.bigint(),
  chainId: v2.number(),
  auctionType: v2.optional(v2.enum_(AuctionType)),
  bidderAddress: AddressSchema,
  referrerAddress: AddressSchema,
  signedPermit2Approval: v2.optional(v2.string()),
  callbackData: BytesSchema
});

// src/core/bid/abi.ts
var abi_exports = {};
__export(abi_exports, {
  ENCRYPTED_BID_TYPE_ABI: () => ENCRYPTED_BID_TYPE_ABI
});
var ENCRYPTED_BID_TYPE_ABI = [
  { name: "encryptedBid", type: "uint256" },
  {
    name: "bidPublicKey",
    type: "tuple",
    internalType: "struct Point",
    components: [
      {
        name: "x",
        type: "uint256"
      },
      {
        name: "y",
        type: "uint256"
      }
    ]
  }
];

// src/core/bid/utils.ts
import { fromHex, toHex, encodeAbiParameters } from "viem";

// src/types.ts
import * as v6 from "valibot";

// ../../node_modules/@trpc/server/dist/getCauseFromUnknown-2d66414a.mjs
function isObject(value) {
  return !!value && !Array.isArray(value) && typeof value === "object";
}
var UnknownCauseError = class extends Error {
};
function getCauseFromUnknown(cause) {
  if (cause instanceof Error) {
    return cause;
  }
  const type = typeof cause;
  if (type === "undefined" || type === "function" || cause === null) {
    return void 0;
  }
  if (type !== "object") {
    return new Error(String(cause));
  }
  if (isObject(cause)) {
    const err = new UnknownCauseError();
    for (const key in cause) {
      err[key] = cause[key];
    }
    return err;
  }
  return void 0;
}

// ../../node_modules/@trpc/server/dist/TRPCError-98d44758.mjs
function getTRPCErrorFromUnknown(cause) {
  if (cause instanceof TRPCError) {
    return cause;
  }
  if (cause instanceof Error && cause.name === "TRPCError") {
    return cause;
  }
  const trpcError = new TRPCError({
    code: "INTERNAL_SERVER_ERROR",
    cause
  });
  if (cause instanceof Error && cause.stack) {
    trpcError.stack = cause.stack;
  }
  return trpcError;
}
var TRPCError = class extends Error {
  constructor(opts) {
    const cause = getCauseFromUnknown(opts.cause);
    const message = opts.message ?? cause?.message ?? opts.code;
    super(message, {
      cause
    });
    this.code = opts.code;
    this.name = "TRPCError";
    if (!this.cause) {
      this.cause = cause;
    }
  }
};

// ../../node_modules/@trpc/server/dist/codes-c924c3db.mjs
function invert(obj) {
  const newObj = /* @__PURE__ */ Object.create(null);
  for (const key in obj) {
    const v17 = obj[key];
    newObj[v17] = key;
  }
  return newObj;
}
var TRPC_ERROR_CODES_BY_KEY = {
  /**
  * Invalid JSON was received by the server.
  * An error occurred on the server while parsing the JSON text.
  */
  PARSE_ERROR: -32700,
  /**
  * The JSON sent is not a valid Request object.
  */
  BAD_REQUEST: -32600,
  // Internal JSON-RPC error
  INTERNAL_SERVER_ERROR: -32603,
  NOT_IMPLEMENTED: -32603,
  // Implementation specific errors
  UNAUTHORIZED: -32001,
  FORBIDDEN: -32003,
  NOT_FOUND: -32004,
  METHOD_NOT_SUPPORTED: -32005,
  TIMEOUT: -32008,
  CONFLICT: -32009,
  PRECONDITION_FAILED: -32012,
  PAYLOAD_TOO_LARGE: -32013,
  UNPROCESSABLE_CONTENT: -32022,
  TOO_MANY_REQUESTS: -32029,
  CLIENT_CLOSED_REQUEST: -32099
};
var TRPC_ERROR_CODES_BY_NUMBER = invert(TRPC_ERROR_CODES_BY_KEY);

// ../../node_modules/@trpc/server/dist/index-f91d720c.mjs
var TRPC_ERROR_CODES_BY_NUMBER2 = invert(TRPC_ERROR_CODES_BY_KEY);
var JSONRPC2_TO_HTTP_CODE = {
  PARSE_ERROR: 400,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  METHOD_NOT_SUPPORTED: 405,
  TIMEOUT: 408,
  CONFLICT: 409,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  UNPROCESSABLE_CONTENT: 422,
  TOO_MANY_REQUESTS: 429,
  CLIENT_CLOSED_REQUEST: 499,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501
};
function getStatusCodeFromKey(code) {
  return JSONRPC2_TO_HTTP_CODE[code] ?? 500;
}
function getHTTPStatusCodeFromError(error) {
  return getStatusCodeFromKey(error.code);
}
var noop = () => {
};
function createInnerProxy(callback, path) {
  const proxy = new Proxy(noop, {
    get(_obj, key) {
      if (typeof key !== "string" || key === "then") {
        return void 0;
      }
      return createInnerProxy(callback, [
        ...path,
        key
      ]);
    },
    apply(_1, _2, args) {
      const isApply = path[path.length - 1] === "apply";
      return callback({
        args: isApply ? args.length >= 2 ? args[1] : [] : args,
        path: isApply ? path.slice(0, -1) : path
      });
    }
  });
  return proxy;
}
var createRecursiveProxy = (callback) => createInnerProxy(callback, []);
var createFlatProxy = (callback) => {
  return new Proxy(noop, {
    get(_obj, name) {
      if (typeof name !== "string" || name === "then") {
        return void 0;
      }
      return callback(name);
    }
  });
};

// ../../node_modules/@trpc/server/dist/config-d5fdbd39.mjs
function getDataTransformer(transformer) {
  if ("input" in transformer) {
    return transformer;
  }
  return {
    input: transformer,
    output: transformer
  };
}
var defaultTransformer = {
  _default: true,
  input: {
    serialize: (obj) => obj,
    deserialize: (obj) => obj
  },
  output: {
    serialize: (obj) => obj,
    deserialize: (obj) => obj
  }
};
var defaultFormatter = ({ shape }) => {
  return shape;
};
function omitPrototype(obj) {
  return Object.assign(/* @__PURE__ */ Object.create(null), obj);
}
var procedureTypes = [
  "query",
  "mutation",
  "subscription"
];
function isRouter(procedureOrRouter) {
  return "router" in procedureOrRouter._def;
}
var emptyRouter = {
  _ctx: null,
  _errorShape: null,
  _meta: null,
  queries: {},
  mutations: {},
  subscriptions: {},
  errorFormatter: defaultFormatter,
  transformer: defaultTransformer
};
var reservedWords = [
  /**
  * Then is a reserved word because otherwise we can't return a promise that returns a Proxy
  * since JS will think that `.then` is something that exists
  */
  "then"
];
function createRouterFactory(config) {
  return function createRouterInner(procedures) {
    const reservedWordsUsed = new Set(Object.keys(procedures).filter((v17) => reservedWords.includes(v17)));
    if (reservedWordsUsed.size > 0) {
      throw new Error("Reserved words used in `router({})` call: " + Array.from(reservedWordsUsed).join(", "));
    }
    const routerProcedures = omitPrototype({});
    function recursiveGetPaths(procedures2, path = "") {
      for (const [key, procedureOrRouter] of Object.entries(procedures2 ?? {})) {
        const newPath = `${path}${key}`;
        if (isRouter(procedureOrRouter)) {
          recursiveGetPaths(procedureOrRouter._def.procedures, `${newPath}.`);
          continue;
        }
        if (routerProcedures[newPath]) {
          throw new Error(`Duplicate key: ${newPath}`);
        }
        routerProcedures[newPath] = procedureOrRouter;
      }
    }
    recursiveGetPaths(procedures);
    const _def = {
      _config: config,
      router: true,
      procedures: routerProcedures,
      ...emptyRouter,
      record: procedures,
      queries: Object.entries(routerProcedures).filter((pair) => pair[1]._def.query).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {}),
      mutations: Object.entries(routerProcedures).filter((pair) => pair[1]._def.mutation).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {}),
      subscriptions: Object.entries(routerProcedures).filter((pair) => pair[1]._def.subscription).reduce((acc, [key, val]) => ({
        ...acc,
        [key]: val
      }), {})
    };
    const router2 = {
      ...procedures,
      _def,
      createCaller(ctx) {
        return createCallerFactory()(router2)(ctx);
      },
      getErrorShape(opts) {
        const { path, error } = opts;
        const { code } = opts.error;
        const shape = {
          message: error.message,
          code: TRPC_ERROR_CODES_BY_KEY[code],
          data: {
            code,
            httpStatus: getHTTPStatusCodeFromError(error)
          }
        };
        if (config.isDev && typeof opts.error.stack === "string") {
          shape.data.stack = opts.error.stack;
        }
        if (typeof path === "string") {
          shape.data.path = path;
        }
        return this._def._config.errorFormatter({
          ...opts,
          shape
        });
      }
    };
    return router2;
  };
}
function callProcedure(opts) {
  const { type, path } = opts;
  if (!(path in opts.procedures) || !opts.procedures[path]?._def[type]) {
    throw new TRPCError({
      code: "NOT_FOUND",
      message: `No "${type}"-procedure on path "${path}"`
    });
  }
  const procedure = opts.procedures[path];
  return procedure(opts);
}
function createCallerFactory() {
  return function createCallerInner(router2) {
    const def = router2._def;
    return function createCaller(ctx) {
      const proxy = createRecursiveProxy(({ path, args }) => {
        if (path.length === 1 && procedureTypes.includes(path[0])) {
          return callProcedure({
            procedures: def.procedures,
            path: args[0],
            rawInput: args[1],
            ctx,
            type: path[0]
          });
        }
        const fullPath = path.join(".");
        const procedure = def.procedures[fullPath];
        let type = "query";
        if (procedure._def.mutation) {
          type = "mutation";
        } else if (procedure._def.subscription) {
          type = "subscription";
        }
        return procedure({
          path: fullPath,
          rawInput: args[0],
          ctx,
          type
        });
      });
      return proxy;
    };
  };
}
var isServerDefault = typeof window === "undefined" || "Deno" in window || globalThis.process?.env?.NODE_ENV === "test" || !!globalThis.process?.env?.JEST_WORKER_ID || !!globalThis.process?.env?.VITEST_WORKER_ID;

// ../../node_modules/@trpc/server/dist/index.mjs
function getParseFn(procedureParser) {
  const parser2 = procedureParser;
  if (typeof parser2 === "function") {
    return parser2;
  }
  if (typeof parser2.parseAsync === "function") {
    return parser2.parseAsync.bind(parser2);
  }
  if (typeof parser2.parse === "function") {
    return parser2.parse.bind(parser2);
  }
  if (typeof parser2.validateSync === "function") {
    return parser2.validateSync.bind(parser2);
  }
  if (typeof parser2.create === "function") {
    return parser2.create.bind(parser2);
  }
  if (typeof parser2.assert === "function") {
    return (value) => {
      parser2.assert(value);
      return value;
    };
  }
  throw new Error("Could not find a validator fn");
}
function mergeWithoutOverrides(obj1, ...objs) {
  const newObj = Object.assign(/* @__PURE__ */ Object.create(null), obj1);
  for (const overrides of objs) {
    for (const key in overrides) {
      if (key in newObj && newObj[key] !== overrides[key]) {
        throw new Error(`Duplicate key ${key}`);
      }
      newObj[key] = overrides[key];
    }
  }
  return newObj;
}
function createMiddlewareFactory() {
  function createMiddlewareInner(middlewares) {
    return {
      _middlewares: middlewares,
      unstable_pipe(middlewareBuilderOrFn) {
        const pipedMiddleware = "_middlewares" in middlewareBuilderOrFn ? middlewareBuilderOrFn._middlewares : [
          middlewareBuilderOrFn
        ];
        return createMiddlewareInner([
          ...middlewares,
          ...pipedMiddleware
        ]);
      }
    };
  }
  function createMiddleware(fn) {
    return createMiddlewareInner([
      fn
    ]);
  }
  return createMiddleware;
}
function isPlainObject(obj) {
  return obj && typeof obj === "object" && !Array.isArray(obj);
}
function createInputMiddleware(parse) {
  const inputMiddleware = async ({ next, rawInput, input }) => {
    let parsedInput;
    try {
      parsedInput = await parse(rawInput);
    } catch (cause) {
      throw new TRPCError({
        code: "BAD_REQUEST",
        cause
      });
    }
    const combinedInput = isPlainObject(input) && isPlainObject(parsedInput) ? {
      ...input,
      ...parsedInput
    } : parsedInput;
    return next({
      input: combinedInput
    });
  };
  inputMiddleware._type = "input";
  return inputMiddleware;
}
function createOutputMiddleware(parse) {
  const outputMiddleware = async ({ next }) => {
    const result = await next();
    if (!result.ok) {
      return result;
    }
    try {
      const data = await parse(result.data);
      return {
        ...result,
        data
      };
    } catch (cause) {
      throw new TRPCError({
        message: "Output validation failed",
        code: "INTERNAL_SERVER_ERROR",
        cause
      });
    }
  };
  outputMiddleware._type = "output";
  return outputMiddleware;
}
var middlewareMarker = "middlewareMarker";
function createNewBuilder(def1, def2) {
  const { middlewares = [], inputs, meta, ...rest } = def2;
  return createBuilder({
    ...mergeWithoutOverrides(def1, rest),
    inputs: [
      ...def1.inputs,
      ...inputs ?? []
    ],
    middlewares: [
      ...def1.middlewares,
      ...middlewares
    ],
    meta: def1.meta && meta ? {
      ...def1.meta,
      ...meta
    } : meta ?? def1.meta
  });
}
function createBuilder(initDef = {}) {
  const _def = {
    inputs: [],
    middlewares: [],
    ...initDef
  };
  return {
    _def,
    input(input) {
      const parser2 = getParseFn(input);
      return createNewBuilder(_def, {
        inputs: [
          input
        ],
        middlewares: [
          createInputMiddleware(parser2)
        ]
      });
    },
    output(output) {
      const parseOutput = getParseFn(output);
      return createNewBuilder(_def, {
        output,
        middlewares: [
          createOutputMiddleware(parseOutput)
        ]
      });
    },
    meta(meta) {
      return createNewBuilder(_def, {
        meta
      });
    },
    /**
    * @deprecated
    * This functionality is deprecated and will be removed in the next major version.
    */
    unstable_concat(builder) {
      return createNewBuilder(_def, builder._def);
    },
    use(middlewareBuilderOrFn) {
      const middlewares = "_middlewares" in middlewareBuilderOrFn ? middlewareBuilderOrFn._middlewares : [
        middlewareBuilderOrFn
      ];
      return createNewBuilder(_def, {
        middlewares
      });
    },
    query(resolver) {
      return createResolver({
        ..._def,
        query: true
      }, resolver);
    },
    mutation(resolver) {
      return createResolver({
        ..._def,
        mutation: true
      }, resolver);
    },
    subscription(resolver) {
      return createResolver({
        ..._def,
        subscription: true
      }, resolver);
    }
  };
}
function createResolver(_def, resolver) {
  const finalBuilder = createNewBuilder(_def, {
    resolver,
    middlewares: [
      async function resolveMiddleware(opts) {
        const data = await resolver(opts);
        return {
          marker: middlewareMarker,
          ok: true,
          data,
          ctx: opts.ctx
        };
      }
    ]
  });
  return createProcedureCaller(finalBuilder._def);
}
var codeblock = `
This is a client-only function.
If you want to call this function on the server, see https://trpc.io/docs/server/server-side-calls
`.trim();
function createProcedureCaller(_def) {
  const procedure = async function resolve(opts) {
    if (!opts || !("rawInput" in opts)) {
      throw new Error(codeblock);
    }
    const callRecursive = async (callOpts = {
      index: 0,
      ctx: opts.ctx
    }) => {
      try {
        const middleware = _def.middlewares[callOpts.index];
        const result2 = await middleware({
          ctx: callOpts.ctx,
          type: opts.type,
          path: opts.path,
          rawInput: callOpts.rawInput ?? opts.rawInput,
          meta: _def.meta,
          input: callOpts.input,
          next(_nextOpts) {
            const nextOpts = _nextOpts;
            return callRecursive({
              index: callOpts.index + 1,
              ctx: nextOpts && "ctx" in nextOpts ? {
                ...callOpts.ctx,
                ...nextOpts.ctx
              } : callOpts.ctx,
              input: nextOpts && "input" in nextOpts ? nextOpts.input : callOpts.input,
              rawInput: nextOpts && "rawInput" in nextOpts ? nextOpts.rawInput : callOpts.rawInput
            });
          }
        });
        return result2;
      } catch (cause) {
        return {
          ok: false,
          error: getTRPCErrorFromUnknown(cause),
          marker: middlewareMarker
        };
      }
    };
    const result = await callRecursive();
    if (!result) {
      throw new TRPCError({
        code: "INTERNAL_SERVER_ERROR",
        message: "No result from middlewares - did you forget to `return next()`?"
      });
    }
    if (!result.ok) {
      throw result.error;
    }
    return result.data;
  };
  procedure._def = _def;
  procedure.meta = _def.meta;
  return procedure;
}
function mergeRouters(...routerList) {
  const record = mergeWithoutOverrides({}, ...routerList.map((r) => r._def.record));
  const errorFormatter = routerList.reduce((currentErrorFormatter, nextRouter) => {
    if (nextRouter._def._config.errorFormatter && nextRouter._def._config.errorFormatter !== defaultFormatter) {
      if (currentErrorFormatter !== defaultFormatter && currentErrorFormatter !== nextRouter._def._config.errorFormatter) {
        throw new Error("You seem to have several error formatters");
      }
      return nextRouter._def._config.errorFormatter;
    }
    return currentErrorFormatter;
  }, defaultFormatter);
  const transformer = routerList.reduce((prev, current) => {
    if (current._def._config.transformer && current._def._config.transformer !== defaultTransformer) {
      if (prev !== defaultTransformer && prev !== current._def._config.transformer) {
        throw new Error("You seem to have several transformers");
      }
      return current._def._config.transformer;
    }
    return prev;
  }, defaultTransformer);
  const router2 = createRouterFactory({
    errorFormatter,
    transformer,
    isDev: routerList.some((r) => r._def._config.isDev),
    allowOutsideOfServer: routerList.some((r) => r._def._config.allowOutsideOfServer),
    isServer: routerList.some((r) => r._def._config.isServer),
    $types: routerList[0]?._def._config.$types
  })(record);
  return router2;
}
var TRPCBuilder = class _TRPCBuilder {
  context() {
    return new _TRPCBuilder();
  }
  meta() {
    return new _TRPCBuilder();
  }
  create(options) {
    return createTRPCInner()(options);
  }
};
var initTRPC = new TRPCBuilder();
function createTRPCInner() {
  return function initTRPCInner(runtime) {
    const errorFormatter = runtime?.errorFormatter ?? defaultFormatter;
    const transformer = getDataTransformer(runtime?.transformer ?? defaultTransformer);
    const config = {
      transformer,
      isDev: runtime?.isDev ?? globalThis.process?.env?.NODE_ENV !== "production",
      allowOutsideOfServer: runtime?.allowOutsideOfServer ?? false,
      errorFormatter,
      isServer: runtime?.isServer ?? isServerDefault,
      /**
      * @internal
      */
      $types: createFlatProxy((key) => {
        throw new Error(`Tried to access "$types.${key}" which is not available at runtime`);
      })
    };
    {
      const isServer = runtime?.isServer ?? isServerDefault;
      if (!isServer && runtime?.allowOutsideOfServer !== true) {
        throw new Error(`You're trying to use @trpc/server in a non-server environment. This is not supported by default.`);
      }
    }
    return {
      /**
      * These are just types, they can't be used
      * @internal
      */
      _config: config,
      /**
      * Builder object for creating procedures
      * @see https://trpc.io/docs/server/procedures
      */
      procedure: createBuilder({
        meta: runtime?.defaultMeta
      }),
      /**
      * Create reusable middlewares
      * @see https://trpc.io/docs/server/middlewares
      */
      middleware: createMiddlewareFactory(),
      /**
      * Create a router
      * @see https://trpc.io/docs/server/routers
      */
      router: createRouterFactory(config),
      /**
      * Merge Routers
      * @see https://trpc.io/docs/server/merging-routers
      */
      mergeRouters,
      /**
      * Create a server-side caller for a router
      * @see https://trpc.io/docs/server/server-side-calls
      */
      createCallerFactory: createCallerFactory()
    };
  };
}

// src/core/create/schema.ts
import * as v5 from "valibot";

// src/modules/auctions/fixed-price/schema.ts
import * as v3 from "valibot";
import { AuctionType as AuctionType2 } from "@axis-finance/types";
var schema2 = v3.object({
  type: v3.literal(AuctionType2.FIXED_PRICE_BATCH),
  price: v3.bigint(),
  minFillPercent: v3.number()
});

// src/modules/auctions/sealed-bid/schema.ts
import * as v4 from "valibot";
import { AuctionType as AuctionType3 } from "@axis-finance/types";
var schema3 = v4.object({
  type: v4.literal(AuctionType3.SEALED_BID),
  minFillPercent: v4.number(),
  minBidSize: v4.bigint(),
  minPrice: v4.bigint(),
  publicKey: v4.object({
    x: v4.bigint(),
    y: v4.bigint()
  })
});

// src/core/create/schema.ts
var Bytes5Schema = v5.custom(
  (input) => typeof input === "string" ? /^0x[a-fA-F0-9]*$/.test(input) : false
);
var AuctionMetadataSchema = v5.object({
  id: v5.optional(v5.string()),
  name: v5.string(),
  description: v5.string(),
  tagline: v5.string(),
  allowlist: v5.optional(v5.array(v5.array(v5.string()))),
  links: v5.object({
    projectLogo: v5.pipe(v5.string(), v5.url()),
    payoutTokenLogo: v5.pipe(v5.string(), v5.url()),
    projectBanner: v5.pipe(v5.string(), v5.url()),
    website: v5.pipe(v5.string(), v5.url()),
    twitter: v5.optional(v5.pipe(v5.string(), v5.url())),
    discord: v5.optional(v5.pipe(v5.string(), v5.url())),
    farcaster: v5.optional(v5.pipe(v5.string(), v5.url()))
  })
});
var CommonAuctionSchema = v5.object({
  curator: AddressSchema,
  referrerFee: v5.number(),
  startDate: v5.date(),
  endDate: v5.date(),
  isCapacityDenominatedInQuoteToken: v5.optional(v5.boolean()),
  capacity: v5.string(),
  metadata: AuctionMetadataSchema
});
var schema4 = v5.object({
  chainId: v5.number(),
  auction: v5.variant("type", [
    v5.object({
      ...CommonAuctionSchema.entries,
      ...schema3.entries
    }),
    v5.object({
      ...CommonAuctionSchema.entries,
      ...schema2.entries
    })
  ]),
  tokens: v5.object({
    base: v5.object({
      address: AddressSchema,
      decimals: v5.number()
    }),
    quote: v5.object({
      address: AddressSchema,
      decimals: v5.number()
    })
  }),
  // TODO: handle specific callback modules like we did auction modules
  callback: v5.object({
    address: AddressSchema,
    data: BytesSchema
  }),
  // TODO: handle specific derivative modules like we did auction modules
  derivative: v5.object({
    type: Bytes5Schema,
    params: BytesSchema,
    wrap: v5.boolean()
  })
});

// src/utils/metadata-client.ts
import {
  FleekSdk,
  ApplicationAccessTokenService
} from "@fleek-platform/sdk/browser";

// src/types.ts
var SdkError = class extends Error {
  issues;
  constructor(message, issues = void 0) {
    super(message);
    this.name = "OriginSdkError";
    this.issues = issues;
  }
};
var t = initTRPC.create({
  isServer: false,
  allowOutsideOfServer: true
});
var CuratorProfileSchema = v6.object({
  curatorProfilePayload: v6.string(),
  ipfsCid: v6.string()
});
var router = t.router({
  storeAuctionInfo: t.procedure.input(v6.parser(AuctionMetadataSchema)).output(v6.parser(v6.string())).mutation(() => ""),
  bustAxisFollowingCache: t.procedure.input(v6.parser(v6.object({ pwd: v6.string() }))).query(() => true),
  axisFollowing: t.procedure.input(v6.parser(v6.void())).query(() => true),
  getSigningSignatureForCurator: t.procedure.input(v6.parser(CuratorProfileSchema)).output(
    v6.parser(
      v6.object({
        signature: v6.string()
      })
    )
  ).mutation(() => ({
    signature: ""
  }))
});

// src/core/bid/utils.ts
import { getAuctionHouse } from "@axis-finance/deployments";
var getEncryptedBid = async (params, cloakClient) => {
  let result;
  try {
    const { lotId, amountIn, amountOut, chainId, bidderAddress, auctionType } = params;
    const auctionHouseAddress = getAuctionHouse({
      chainId,
      auctionType
    })?.address;
    result = await cloakClient.keysApi.encryptLotIdPost({
      xChainId: chainId,
      xAuctionHouse: auctionHouseAddress,
      lotId,
      encryptRequest: {
        amount: toHex(amountIn),
        amountOut: toHex(amountOut),
        bidder: bidderAddress
      }
    });
  } catch (error) {
    console.error(error);
    throw new SdkError("Failed to encrypt bid via cloak service");
  }
  if (!result || !result.ciphertext || !result.x || !result.y) {
    throw new SdkError("Failed to encrypt bid via cloak service");
  }
  return result;
};
var encodeEncryptedBid = (encryptedBid) => {
  return encodeAbiParameters(abi_exports.ENCRYPTED_BID_TYPE_ABI, [
    fromHex(encryptedBid.ciphertext, "bigint"),
    {
      x: fromHex(encryptedBid.x, "bigint"),
      y: fromHex(encryptedBid.y, "bigint")
    }
  ]);
};

// src/core/bid/get-config.ts
import * as v7 from "valibot";
import { AuctionType as AuctionType4 } from "@axis-finance/types";
import { abis } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse2 } from "@axis-finance/deployments";
import { toHex as toHex2 } from "viem";
var getConfig = async (params, cloak) => {
  const parsedParams = v7.safeParse(schema, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const {
    lotId,
    amountIn,
    amountOut,
    bidderAddress,
    referrerAddress,
    chainId,
    auctionType,
    callbackData
  } = params;
  const auctionHouseAddress = getAuctionHouse2({
    chainId,
    auctionType
  })?.address;
  if (auctionHouseAddress === void 0 || auctionHouseAddress === "0x") {
    throw new SdkError(
      `Auction house contract address not found for chainId ${chainId} and auctionType ${auctionType}`
    );
  }
  const shouldEncryptBid = auctionType === AuctionType4.SEALED_BID;
  const paramsToEncrypt = {
    lotId,
    amountIn,
    amountOut,
    chainId,
    bidderAddress,
    auctionType
  };
  const encryptedBid = shouldEncryptBid ? encodeEncryptedBid(await getEncryptedBid(paramsToEncrypt, cloak)) : void 0;
  return {
    abi: abis.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "bid",
    args: [
      {
        lotId: BigInt(lotId),
        bidder: bidderAddress,
        referrer: referrerAddress,
        amount: amountIn,
        auctionData: encryptedBid || toHex2(""),
        permit2Data: toHex2("")
        // TODO: handle permit2Data
      },
      callbackData
    ]
  };
};

// src/react/hooks/use-axis-transaction.ts
import * as v8 from "valibot";
import {
  useSimulateContract,
  useWaitForTransactionReceipt,
  useWriteContract
} from "wagmi";
import { useQuery as useQuery2 } from "@tanstack/react-query";
function useAxisTransaction(params, paramsSchema, functionName, getConfig6, deps) {
  const parsedParams = v8.safeParse(paramsSchema, params);
  const config = useQuery2({
    queryKey: ["config", functionName, params],
    queryFn: () => deps ? getConfig6(params, deps) : getConfig6(params),
    enabled: parsedParams.success
  });
  const transaction = useWriteContract();
  const simulation = useSimulateContract({
    abi: config?.data?.abi,
    address: config?.data?.address,
    chainId: params.chainId,
    functionName: config?.data?.functionName,
    args: config?.data?.args,
    query: {
      enabled: config != null && config.data != null && parsedParams.success
    }
  });
  const receipt = useWaitForTransactionReceipt({
    hash: transaction.data,
    query: {
      enabled: config != null && parsedParams.success && simulation.isSuccess
    }
  });
  const submit = () => {
    if (simulation.data && config.data != null) {
      transaction.writeContract(config.data);
    }
  };
  const isWaiting = transaction.isPending || receipt.isLoading;
  const error = simulation.error || transaction.error || receipt.error || config.error;
  return {
    submit,
    simulation,
    transaction,
    receipt,
    isWaiting,
    error
  };
}

// src/react/hooks/use-bid.ts
function useBid(params) {
  const { cloakClient } = useSdk();
  return useAxisTransaction(params, schema, "bid", getConfig, cloakClient);
}

// src/core/abort/schema.ts
import { AuctionType as AuctionType5 } from "@axis-finance/types";
import * as v9 from "valibot";
var schema5 = v9.object({
  lotId: v9.number(),
  chainId: v9.number(),
  auctionType: v9.enum_(AuctionType5)
});

// src/core/abort/get-config.ts
import * as v10 from "valibot";
import { abis as abis2 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse3 } from "@axis-finance/deployments";
var getConfig2 = (params) => {
  const parsedParams = v10.safeParse(schema5, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse3({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis2.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "abort",
    args: [BigInt(lotId)]
  };
};

// src/react/hooks/use-cancel.ts
function useAbort(params) {
  return useAxisTransaction(params, schema5, "abort", getConfig2);
}

// src/core/claim-bids/schema.ts
import * as v11 from "valibot";
import { AuctionType as AuctionType6 } from "@axis-finance/types";
var schema6 = v11.object({
  lotId: v11.number(),
  bids: v11.array(v11.number()),
  chainId: v11.number(),
  auctionType: v11.optional(v11.enum_(AuctionType6))
});

// src/core/claim-bids/get-config.ts
import * as v12 from "valibot";
import { abis as abis3 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse4 } from "@axis-finance/deployments";
var getConfig3 = (params) => {
  const parsedParams = v12.safeParse(schema6, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, bids, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse4({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis3.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "claimBids",
    args: [BigInt(lotId), bids.map(BigInt)]
  };
};

// src/react/hooks/use-claim-bids.ts
function useClaimBids(params) {
  return useAxisTransaction(params, schema6, "claimBids", getConfig3);
}

// src/core/refund-bid/schema.ts
import * as v13 from "valibot";
import { AuctionType as AuctionType7 } from "@axis-finance/types";
var schema7 = v13.object({
  lotId: v13.number(),
  bidId: v13.number(),
  bidIndex: v13.number(),
  chainId: v13.number(),
  auctionType: v13.optional(v13.enum_(AuctionType7))
});

// src/core/refund-bid/get-config.ts
import * as v14 from "valibot";
import { abis as abis4 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse5 } from "@axis-finance/deployments";
var getConfig4 = (params) => {
  const parsedParams = v14.safeParse(schema7, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, bidId, bidIndex, chainId, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse5({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis4.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "refundBid",
    args: [BigInt(lotId), BigInt(bidId), BigInt(bidIndex)]
  };
};

// src/react/hooks/use-refund-bid.ts
function useRefundBid(params) {
  return useAxisTransaction(params, schema7, "refundBid", getConfig4);
}

// src/core/settle/schema.ts
import { AuctionType as AuctionType8 } from "@axis-finance/types";
import * as v15 from "valibot";
var schema8 = v15.object({
  lotId: v15.number(),
  numBids: v15.number(),
  chainId: v15.number(),
  callbackData: AddressSchema,
  auctionType: v15.optional(v15.enum_(AuctionType8))
});

// src/core/settle/get-config.ts
import * as v16 from "valibot";
import { abis as abis5 } from "@axis-finance/abis";
import { getAuctionHouse as getAuctionHouse6 } from "@axis-finance/deployments";
var getConfig5 = (params) => {
  const parsedParams = v16.safeParse(schema8, params);
  if (!parsedParams.success) {
    throw new SdkError(
      "Invalid parameters supplied to getConfig()",
      parsedParams.issues
    );
  }
  const { lotId, numBids, chainId, callbackData, auctionType } = params;
  const auctionHouseAddress = getAuctionHouse6({
    chainId,
    auctionType
  })?.address;
  return {
    abi: abis5.batchAuctionHouse,
    address: auctionHouseAddress,
    functionName: "settle",
    args: [BigInt(lotId), BigInt(numBids), callbackData]
  };
};

// src/react/hooks/use-settle.ts
function useSettle(params) {
  return useAxisTransaction(params, schema8, "settle", getConfig5);
}
export {
  OriginSdkContext,
  OriginSdkProvider,
  useAbort,
  useBid,
  useClaimBids,
  useDeferredQuery,
  useLaunchQuery,
  useLaunchesQuery,
  useRefundBid,
  useSdk,
  useSdkQuery,
  useSettle,
  useTokenLaunchesQuery
};
//# sourceMappingURL=index.js.map